import { Text } from '@lmc/design-system'
import { cssVars } from '@lmc/design-system/tokens'
import {
  CONTRIBUTING_FACTOR_GROUPS,
  ContributingFactorEnum,
  ContributingFactorGroupEnum
} from '@lmc/validations'
import { Checkbox } from 'antd'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import StepField from '../../StepField'
import { stepChoiceColumn } from '../../stepLayout'

const { spacing } = cssVars

/** The design's group order; the shared map is keyed alphabetically. */
export const GROUP_ORDER: ReadonlyArray<ContributingFactorGroupEnum> = [
  ContributingFactorGroupEnum.people,
  ContributingFactorGroupEnum.process,
  ContributingFactorGroupEnum.environment,
  ContributingFactorGroupEnum.equipment,
  ContributingFactorGroupEnum.organisation
]

export const GROUP_KEYS: Record<ContributingFactorGroupEnum, string> = {
  [ContributingFactorGroupEnum.environment]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_GROUP_ENVIRONMENT,
  [ContributingFactorGroupEnum.equipment]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_GROUP_EQUIPMENT,
  [ContributingFactorGroupEnum.organisation]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_GROUP_ORGANISATION,
  [ContributingFactorGroupEnum.people]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_GROUP_PEOPLE,
  [ContributingFactorGroupEnum.process]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_GROUP_PROCESS
}

export const FACTOR_KEYS: Record<ContributingFactorEnum, string> = {
  [ContributingFactorEnum.communication]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_COMMUNICATION,
  [ContributingFactorEnum.competency]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_COMPETENCY,
  [ContributingFactorEnum.culture]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_CULTURE,
  [ContributingFactorEnum.distractions]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_DISTRACTIONS,
  [ContributingFactorEnum.documentation]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_DOCUMENTATION,
  [ContributingFactorEnum.equipmentFailure]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_EQUIPMENT_FAILURE,
  [ContributingFactorEnum.equipmentUnavailable]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_EQUIPMENT_UNAVAILABLE,
  [ContributingFactorEnum.fatigue]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_FATIGUE,
  [ContributingFactorEnum.handoverFailures]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_HANDOVER_FAILURES,
  [ContributingFactorEnum.knowledge]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_KNOWLEDGE,
  [ContributingFactorEnum.maintenanceIssues]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_MAINTENANCE_ISSUES,
  [ContributingFactorEnum.noise]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_NOISE,
  [ContributingFactorEnum.physicalLayout]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_PHYSICAL_LAYOUT,
  [ContributingFactorEnum.policies]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_POLICIES,
  [ContributingFactorEnum.poorProcedures]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_POOR_PROCEDURES,
  [ContributingFactorEnum.rostering]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_ROSTERING,
  [ContributingFactorEnum.staffingLevels]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_STAFFING_LEVELS,
  [ContributingFactorEnum.supervision]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_SUPERVISION,
  [ContributingFactorEnum.training]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_TRAINING,
  [ContributingFactorEnum.unclearResponsibilities]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_UNCLEAR_RESPONSIBILITIES,
  [ContributingFactorEnum.workload]:
    incidentReportsConstants.CASE_V2_INVESTIGATION_FACTOR_WORKLOAD
}

/**
 * The contributing factors: one multi-select of codes, shown as a labelled
 * column of checkboxes per group. Three columns wide, one on a phone.
 */
const ContributingFactors = ({ testId }: { testId: string }) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const fieldTestId = `${testId}-contributing-factors`
  const label = t(
    incidentReportsConstants.CASE_V2_INVESTIGATION_CONTRIBUTING_FACTORS
  )

  return (
    <StepField
      name="contributingFactors"
      label={label}
      hint={t(
        incidentReportsConstants.CASE_V2_INVESTIGATION_CONTRIBUTING_FACTORS_HINT
      )}
    >
      <FullWidthCheckboxes data-testid={fieldTestId}>
        {/* The field's <label> can't name a div, so the group names itself. */}
        <Groups role="group" aria-label={label}>
          {GROUP_ORDER.map((group) => {
            const labelId = `${fieldTestId}-${group}`

            return (
              <Group key={group} role="group" aria-labelledby={labelId}>
                <Text id={labelId} weight="semibold" data-testid={labelId}>
                  {t(GROUP_KEYS[group])}
                </Text>
                <Factors>
                  {CONTRIBUTING_FACTOR_GROUPS[group].map((factor) => (
                    <Checkbox
                      key={factor}
                      value={factor}
                      data-testid={`${fieldTestId}-${factor}`}
                    >
                      {t(FACTOR_KEYS[factor])}
                    </Checkbox>
                  ))}
                </Factors>
              </Group>
            )
          })}
        </Groups>
      </FullWidthCheckboxes>
    </StepField>
  )
}

/**
 * antd's group is inline-flex, so it shrinks to its content and the grid inside
 * fits only two columns; full width, it fits the designed three.
 */
const FullWidthCheckboxes = styled(Checkbox.Group)`
  && {
    display: flex;
    width: 100%;
  }
`

const Groups = styled.div`
  display: grid;
  gap: ${spacing[200]};
  grid-template-columns: repeat(auto-fill, minmax(184px, 1fr));
  width: 100%;
`

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[100]};
`

const Factors = styled.div`
  ${stepChoiceColumn}
  align-items: flex-start;
`

export default ContributingFactors
